import { Stack } from "@mantine/core"
import { FilterBuilder } from "@tfg/ui"
import dayjs from "dayjs"
import _ from "lodash"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  FindIngestionsFiltersDto,
  findIngestionsFiltersDtoStatusEnum,
  FindIngestionsResponseDto,
} from "~/gen/api"

export type FindIngestionsFiltersType = FindIngestionsFiltersDto & {
  type?: string
}

type IngestionListFiltersProps = {
  ingestions: FindIngestionsResponseDto[]
  filters: FindIngestionsFiltersType
  setFilters: React.Dispatch<React.SetStateAction<FindIngestionsFiltersType>>
}

export const IngestionListFilters = ({
  ingestions,
  filters,
  setFilters,
}: IngestionListFiltersProps) => {
  const { t } = useTranslation("dataTransfer")

  const [userEmails, setUserEmails] = useState<string[]>([])

  const [createdAtOptions, setCreatedAtOptions] = useState<Date[]>([])

  const [fileNameOptions, setFileNameOptions] = useState<string[]>([])

  useEffect(() => {
    if (ingestions) {
      setUserEmails((prev) => [
        ...new Set([
          ...prev,
          ...ingestions
            .map((ingestion) => ingestion.createdBy?.email)
            .filter(Boolean),
        ]),
      ])
      setFileNameOptions((prev) => [
        ...new Set([
          ...prev,
          ...ingestions.map(
            (ingestion) => ingestion.document?.originalFileName
          ),
        ]),
      ])
      setCreatedAtOptions((prev) => {
        const times = [
          ...prev,
          ...ingestions.map((ingestion) => ingestion.createdAt),
        ]
          .map((d) => {
            const result = new Date(d)
            result.setHours(0, 0, 0, 0)
            return result.getTime()
          })
          .sort((a, b) => {
            return b - a
          })
        return _.uniq(times).map((time) => new Date(time))
      })
    }
  }, [ingestions])

  const emailFilterOptions = useMemo(() => {
    return userEmails.map((email) => ({
      value: email,
      label: email,
      key: email,
    }))
  }, [userEmails])

  const createdAtFilterOptions = useMemo(() => {
    return createdAtOptions.map((createdAt) => ({
      label: dayjs(createdAt).format("l"),
      value: createdAt.toString(),
      key: createdAt,
    }))
  }, [createdAtOptions])

  const fileNameFilterOptions = useMemo(() => {
    return fileNameOptions.map((fileName) => ({
      value: fileName,
      label: fileName,
      key: fileName,
    }))
  }, [fileNameOptions])

  return (
    <Stack>
      <FilterBuilder
        title={t("import.list.filters.title")}
        searchPlaceholder={t("import.list.filters.search")}
        filters={filters}
        setFilters={setFilters}
        options={[
          {
            label: t("import.list.table.columns.status.header"),
            key: "status",
            type: "select",
            options: [
              {
                value: findIngestionsFiltersDtoStatusEnum.PENDING,
                label: t("import.list.filters.pending"),
              },
              {
                value: findIngestionsFiltersDtoStatusEnum.ERROR,
                label: t("import.list.filters.error"),
              },
              {
                value: findIngestionsFiltersDtoStatusEnum.FAILED,
                label: t("import.list.filters.failed"),
              },
              {
                value: findIngestionsFiltersDtoStatusEnum.SUCCESS,
                label: t("import.list.filters.success"),
              },
            ],
          },
          {
            label: t("import.list.table.columns.user.header"),
            key: "userEmail",
            options: emailFilterOptions,
            type: "select",
          },
          {
            label: t("import.list.table.columns.date.header"),
            key: "createdAt",
            options: createdAtFilterOptions,
            disableSort: true,
            type: "select",
          },
          {
            label: t("import.list.table.columns.type.header"),
            key: "type",
            type: "select",
            options: [
              {
                label: t("import.legacy.templateCard.title"),
                value: "PRODUCT",
              },
              {
                label: t("import.businessPartner.templateCard.title"),
                value: "BUSINESS_PARTNER",
              },
            ],
          },
          {
            label: t("import.list.table.columns.file.header"),
            key: "fileName",
            options: fileNameFilterOptions,
            type: "select",
          },
        ]}
      />
    </Stack>
  )
}
